import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pause, Play, LogOut, X } from "lucide-react";

export function PauseModal({ open, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Pause className="w-8 h-8 text-orange-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Pause Session
          </h3>
          <p className="text-gray-600">
            Are you sure you want to pause this session? Users won't be able to
            make requests while paused.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-3 px-4 bg-logo hover:bg-[#62187D] text-white rounded-xl font-medium transition-colors"
            onClick={onConfirm}
          >
            Confirm Pause
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function ResumeModal({ open, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Play className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Resume Session
          </h3>
          <p className="text-gray-600">
            Are you sure you want to resume this session? Users will be able to
            make requests again.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors"
            onClick={onConfirm}
          >
            Confirm Resume
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function EndModal({ open, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LogOut className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">End Session</h3>
          <p className="text-gray-600">
            Are you sure you want to end this session? This action cannot be
            undone and all current requests will be lost.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors"
            onClick={onConfirm}
          >
            End Session
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function ExtendModal({ open, onCancel, onExtend, timeOptions = [] }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Extend Session
          </h3>
          <p className="text-sm text-gray-600 mb-4">Choose duration:</p>
          <div className="grid grid-cols-2 gap-3">
            {timeOptions.map((option) => (
              <button
                key={`${option.value}${option.unit}`}
                onClick={() => onExtend(option.value, option.unit)}
                className="py-3 px-4 rounded-xl font-medium text-sm bg-gray-100 text-gray-700 hover:bg-logo hover:text-white transition-all hover:scale-105"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function UsersModal({ open, onClose, users = [] }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-hidden shadow-2xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-bold text-xl lg:text-2xl text-gray-900">
            Active Users ({users.length})
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-2 overflow-y-auto max-h-96">
          {users.map((user) => (
            <div
              key={user._id}
              className="flex items-center gap-3 p-3 hover:bg-gray-50 border rounded-xl transition-colors"
            >
              <img
                src={user.avatar || "/placeholder.svg?height=40&width=40"}
                className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                alt="User avatar"
              />
              <div className="flex-1">
                <p className="font-medium text-gray-800">{user.display_name}</p>
                <p className="text-sm text-green-600 font-medium">● Active</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export const GoLiveModal = ({ open, onCancel, onConfirm }) => {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">Go Live</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to start this live session?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2  rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Go Live
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
